/**
 * Copyright 2025 Lumavec Group LLC
 *
 * All rights reserved. No part of this software may be used, copied, modified,
 * or distributed without explicit written permission. This software is provided
 * "as-is" without warranty of any kind, express or implied, including but not
 * limited to warranties of merchantability or fitness for a particular purpose.
 */

/**
 * Git Provider Operations (GitHub/GitLab)
 * 
 * This module handles all git provider operations including:
 * - Repository cloning and setup
 * - Velocity branch sync and management
 * - Push/pull operations for team collaboration
 * - Conflict detection and resolution
 * - Pull request creation
 */

import type { SourceControlProvider } from '../../types';
import { getProductServerBaseUrl, buildProductApiUrl } from './base';
import type {
  EnsureReposResult,
  SyncVelocityResult,
  ProductForEnsureRepos,
  CloneRepoResult,
  PushVelocityResult,
  PullVelocityResult,
  RemoteVelocityStatusResult,
  CreateVelocityPRResult,
  SyncToMainResult,
  VelocityModeSyncResult,
  WorktreesResult,
  ConflictsResult,
  SyncToMainAnalysisResult,
  BatchResolveResult,
  AIResolutionResult,
  ApplyResolutionsResult,
  CheckLocalChangesResult,
  ResetRepoResult,
} from '../../../server/types';

// ============================================================================
// Repository Setup Operations
// ============================================================================

/**
 * Ensure all product repositories exist and are checked out to velocity branch
 * 
 * This endpoint handles:
 * - Cloning remote repositories if not already present
 * - Initializing local repositories for products without a remote URL
 * - Setting up the velocity branch worktree
 * 
 * @param products - Array of products to ensure repos for
 * @param accessToken - Optional access token for private repos
 * @param provider - Source control provider ('github' or 'gitlab')
 * @param useSSH - If true, use SSH URLs instead of HTTPS (requires SSH key configured)
 * @returns Result with status for each product
 */
export async function ensureProductRepos(
  products: ProductForEnsureRepos[],
  accessToken?: string | null,
  provider: SourceControlProvider = 'github',
  useSSH?: boolean
): Promise<EnsureReposResult> {
  try {
    const apiEndpoint = provider === 'github' ? 'github' : 'gitlab';
    const apiUrl = `${getProductServerBaseUrl()}/api/${apiEndpoint}/ensure-repos`;

    const response = await fetch(apiUrl, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        products,
        accessToken: accessToken || null,
        useSSH: useSSH || false
      }),
    });

    if (!response.ok) {
      const errorData = await response.json();
      return {
        success: false,
        error: errorData.error || `Failed to ensure repos (${response.status})`
      };
    }

    return await response.json();
  } catch (error: any) {
    console.error('[Product API] Error ensuring product repositories:', error);
    return {
      success: false,
      error: error.message || 'Failed to ensure product repositories'
    };
  }
}

/**
 * Clone a single repository as a worktree container
 * 
 * @param repositoryUrl - URL of the repository to clone
 * @param productTitle - Title of the product (for logging)
 * @param repoPath - Optional path within the repo for monorepos
 * @param accessToken - Optional access token for private repos
 * @param provider - Source control provider ('github' or 'gitlab')
 * @param useSSH - If true, use SSH URLs instead of HTTPS (requires SSH key configured)
 * @returns Result with clone status
 */
export async function cloneRepository(
  repositoryUrl: string,
  productTitle?: string,
  repoPath?: string | null,
  accessToken?: string | null,
  provider: SourceControlProvider = 'github',
  useSSH?: boolean
): Promise<CloneRepoResult> {
  try {
    const apiEndpoint = provider === 'github' ? 'github' : 'gitlab';
    const apiUrl = `${getProductServerBaseUrl()}/api/${apiEndpoint}/clone-repo`;

    const response = await fetch(apiUrl, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        repositoryUrl,
        productTitle,
        repoPath,
        accessToken: accessToken || null,
        useSSH: useSSH || false
      }),
    });

    if (!response.ok) {
      const errorData = await response.json();
      return {
        success: false,
        error: errorData.error || `Failed to clone repository (${response.status})`
      };
    }

    return await response.json();
  } catch (error: any) {
    console.error('[Product API] Error cloning repository:', error);
    return {
      success: false,
      error: error.message || 'Failed to clone repository'
    };
  }
}

/**
 * Clean up a deprecated local repository
 * 
 * Call this when a product transitions from a local repo to a remote repo
 * to clean up the local repository that is no longer needed.
 * 
 * @param localRepoUrl - The local repository URL (e.g., "local/velocity-ppm")
 * @param provider - Source control provider ('github' or 'gitlab')
 * @returns Result with cleanup status
 */
export async function cleanupLocalRepo(
  localRepoUrl: string,
  provider: SourceControlProvider = 'github'
): Promise<{ success: boolean; deleted: boolean; path?: string; error?: string }> {
  try {
    const apiEndpoint = provider === 'github' ? 'github' : 'gitlab';
    const apiUrl = `${getProductServerBaseUrl()}/api/${apiEndpoint}/local-repo`;

    const response = await fetch(apiUrl, {
      method: 'DELETE',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ localRepoUrl }),
    });

    if (!response.ok) {
      const errorData = await response.json();
      return {
        success: false,
        deleted: false,
        error: errorData.error || `Failed to clean up local repo (${response.status})`
      };
    }

    return await response.json();
  } catch (error: any) {
    console.error('[Product API] Error cleaning up local repository:', error);
    return {
      success: false,
      deleted: false,
      error: error.message || 'Failed to clean up local repository'
    };
  }
}

/**
 * Push a local repository to a remote provider
 * 
 * Used when a product has a local-only repository that needs to be pushed
 * to GitHub/GitLab for the first time.
 * 
 * @param containerPath - Path to the local repository container (worktree format)
 * @param repositoryUrl - URL of the remote repository to push to
 * @param accessToken - Access token for authentication
 * @param instanceUrl - Optional GitLab instance URL (for self-hosted)
 * @param provider - Source control provider ('github' or 'gitlab')
 * @returns Result with push status
 */
export async function pushLocalRepoToRemote(
  containerPath: string,
  repositoryUrl: string,
  accessToken: string,
  instanceUrl?: string,
  provider: SourceControlProvider = 'github'
): Promise<{ success: boolean; message?: string; error?: string }> {
  try {
    const apiEndpoint = provider === 'github' ? 'github' : 'gitlab';
    const apiUrl = `${getProductServerBaseUrl()}/api/${apiEndpoint}/push-to-remote`;

    const response = await fetch(apiUrl, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        containerPath,
        repositoryUrl,
        accessToken,
        instanceUrl,
      }),
    });

    if (!response.ok) {
      const errorData = await response.json();
      return {
        success: false,
        error: errorData.error || `Failed to push to remote (${response.status})`
      };
    }

    return await response.json();
  } catch (error: any) {
    console.error('[Product API] Error pushing local repository to remote:', error);
    return {
      success: false,
      error: error.message || 'Failed to push local repository to remote'
    };
  }
}

/**
 * Create a new repository on the remote provider and clone it locally
 * 
 * @param name - Name of the repository to create
 * @param description - Optional description for the repository
 * @param isPrivate - Whether the repository should be private
 * @param accessToken - Access token for authentication
 * @param instanceUrl - Optional GitLab instance URL (for self-hosted)
 * @param provider - Source control provider ('github' or 'gitlab')
 * @param organization - Optional GitHub organization or GitLab group to create the repo under
 * @returns Result with repository info and local clone status
 */
export async function createRemoteRepository(
  name: string,
  description: string | undefined,
  isPrivate: boolean,
  accessToken: string,
  instanceUrl?: string,
  provider: SourceControlProvider = 'github',
  organization?: string
): Promise<{ 
  success: boolean; 
  repository?: { 
    fullName: string; 
    cloneUrl: string; 
    htmlUrl: string; 
  };
  localPath?: string;
  error?: string;
}> {
  try {
    const apiEndpoint = provider === 'github' ? 'github' : 'gitlab';
    const apiUrl = `${getProductServerBaseUrl()}/api/${apiEndpoint}/create-repo`;

    const response = await fetch(apiUrl, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        name,
        description,
        isPrivate,
        accessToken,
        instanceUrl,
        organization,
      }),
    });

    if (!response.ok) {
      const errorData = await response.json();
      return {
        success: false,
        error: errorData.error || `Failed to create repository (${response.status})`
      };
    }

    return await response.json();
  } catch (error: any) {
    console.error('[Product API] Error creating remote repository:', error);
    return {
      success: false,
      error: error.message || 'Failed to create remote repository'
    };
  }
}

// ============================================================================
// Velocity Branch Operations
// ============================================================================

/**
 * Sync the velocity branch with the main branch
 * 
 * This endpoint handles:
 * - Resetting velocity branch to match main
 * - Merging main into velocity (with conflict detection)
 * - Semantic merge with AI assistance
 * 
 * @param repositoryUrl - URL of the repository to sync
 * @param strategy - Sync strategy: 'reset' (default), 'merge', or 'semantic'
 * @param provider - Source control provider ('github' or 'gitlab')
 * @returns Result with sync status
 */
export async function syncVelocityBranch(
  repositoryUrl: string,
  strategy: 'reset' | 'merge' | 'semantic' = 'reset',
  provider: SourceControlProvider = 'github'
): Promise<SyncVelocityResult> {
  try {
    const apiEndpoint = provider === 'github' ? 'github' : 'gitlab';
    const apiUrl = `${getProductServerBaseUrl()}/api/${apiEndpoint}/sync-velocity-semantic`;

    const response = await fetch(apiUrl, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        repositoryUrl,
        strategy,
      }),
    });

    if (!response.ok) {
      const errorData = await response.json();
      return {
        success: false,
        error: errorData.error || `Failed to sync velocity branch (${response.status})`
      };
    }

    return await response.json();
  } catch (error: any) {
    console.error('[Product API] Error syncing velocity branch:', error);
    return {
      success: false,
      error: error.message || 'Failed to sync velocity branch'
    };
  }
}

/**
 * Result type for auto-resolve sync
 */
export interface SyncVelocityAutoResult {
  success: boolean;
  action?: 'synced' | 'conflicts' | 'already-up-to-date' | 'error';
  message?: string;
  autoResolved?: boolean;
  resolutions?: Array<{
    entityId: string;
    method: 'semantic-rule' | 'ai';
    confidence: number;
    reasoning: string;
  }>;
  conflicts?: Array<{
    filePath: string;
    conflictType?: string;
  }>;
  autoResolveResult?: {
    resolved: Array<{
      entityId: string;
      method: string;
      confidence: number;
      reasoning: string;
    }>;
    unresolved: Array<{
      entityId: string;
      reason: string;
    }>;
  };
  error?: string;
}

/**
 * Sync the velocity branch with main, automatically resolving conflicts
 * 
 * This is the recommended sync method. It:
 * 1. Attempts semantic merge first
 * 2. If conflicts occur, applies semantic rules (status progression, timestamps)
 * 3. Falls back to AI resolution for complex conflicts
 * 4. Only returns unresolved conflicts if auto-resolution fails
 * 
 * @param repositoryUrl - URL of the repository to sync
 * @param strategy - Sync strategy: 'merge' or 'reset' (default: 'merge')
 * @param autoApplyThreshold - Confidence threshold for auto-applying (default: 0.8)
 * @param useAIFallback - Whether to use AI for conflicts that rules can't resolve (default: true)
 * @param provider - Source control provider ('github' or 'gitlab')
 * @returns Result with sync status and any auto-resolutions applied
 */
export async function syncVelocityBranchAuto(
  repositoryUrl: string,
  strategy: 'merge' | 'reset' = 'merge',
  autoApplyThreshold: number = 0.8,
  useAIFallback: boolean = true,
  provider: SourceControlProvider = 'github'
): Promise<SyncVelocityAutoResult> {
  try {
    const apiEndpoint = provider === 'github' ? 'github' : 'gitlab';
    const apiUrl = `${getProductServerBaseUrl()}/api/${apiEndpoint}/sync-velocity-auto`;

    const response = await fetch(apiUrl, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        repositoryUrl,
        strategy,
        autoApplyThreshold,
        useAIFallback
      }),
    });

    const data = await response.json();

    if (response.status === 409) {
      // Conflicts that couldn't be auto-resolved
      return {
        success: false,
        action: 'conflicts',
        message: data.message || 'Some conflicts could not be auto-resolved',
        conflicts: data.conflicts,
        autoResolveResult: data.autoResolveResult
      };
    }

    if (!response.ok) {
      return {
        success: false,
        action: 'error',
        error: data.error || data.message || `Failed to sync velocity branch (${response.status})`
      };
    }

    return data;
  } catch (error: any) {
    console.error('[Product API] Error in auto-resolve sync:', error);
    return {
      success: false,
      action: 'error',
      error: error.message || 'Failed to sync velocity branch with auto-resolve'
    };
  }
}

/**
 * Push velocity branch to team (shared remote)
 * 
 * @param repositoryUrl - URL of the repository
 * @param accessToken - Access token for authentication
 * @param provider - Source control provider ('github' or 'gitlab')
 * @param instanceUrl - Optional GitLab instance URL
 * @returns Result with push status
 */
export async function pushVelocityToTeam(
  repositoryUrl: string,
  accessToken: string,
  provider: SourceControlProvider = 'github',
  instanceUrl?: string
): Promise<PushVelocityResult> {
  try {
    const apiEndpoint = provider === 'github' ? 'github' : 'gitlab';
    const apiUrl = `${getProductServerBaseUrl()}/api/${apiEndpoint}/push-velocity`;

    const response = await fetch(apiUrl, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        repositoryUrl,
        accessToken,
        instanceUrl
      }),
    });

    const data = await response.json();

    if (response.status === 409) {
      return {
        success: false,
        error: data.message || 'Conflicts detected with remote velocity. Pull changes first.'
      };
    }

    if (!response.ok) {
      return {
        success: false,
        error: data.error || data.message || `Failed to push velocity (${response.status})`
      };
    }

    return data;
  } catch (error: any) {
    console.error('[Product API] Error pushing velocity to team:', error);
    return {
      success: false,
      error: error.message || 'Failed to push velocity to team'
    };
  }
}

/**
 * Pull velocity branch from team (shared remote)
 * 
 * @param repositoryUrl - URL of the repository
 * @param provider - Source control provider ('github' or 'gitlab')
 * @returns Result with pull status and any conflicts
 */
export async function pullVelocityFromTeam(
  repositoryUrl: string,
  provider: SourceControlProvider = 'github'
): Promise<PullVelocityResult> {
  try {
    const apiEndpoint = provider === 'github' ? 'github' : 'gitlab';
    const apiUrl = `${getProductServerBaseUrl()}/api/${apiEndpoint}/pull-velocity`;

    const response = await fetch(apiUrl, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ repositoryUrl }),
    });

    const data = await response.json();

    if (response.status === 409) {
      return {
        success: false,
        action: 'conflicts',
        conflicts: data.conflicts,
        error: data.message || 'Conflicts detected during pull'
      };
    }

    if (!response.ok) {
      return {
        success: false,
        error: data.error || data.message || `Failed to pull velocity (${response.status})`
      };
    }

    return data;
  } catch (error: any) {
    console.error('[Product API] Error pulling velocity from team:', error);
    return {
      success: false,
      error: error.message || 'Failed to pull velocity from team'
    };
  }
}

/**
 * Get remote velocity branch status
 * 
 * @param repositoryUrl - URL of the repository
 * @param provider - Source control provider ('github' or 'gitlab')
 * @returns Remote velocity status for team collaboration
 */
export async function getRemoteVelocityStatus(
  repositoryUrl: string,
  provider: SourceControlProvider = 'github'
): Promise<RemoteVelocityStatusResult> {
  try {
    const apiEndpoint = provider === 'github' ? 'github' : 'gitlab';
    const apiUrl = buildProductApiUrl(`/api/${apiEndpoint}/remote-velocity-status`, { repositoryUrl });

    const response = await fetch(apiUrl);

    if (!response.ok) {
      return {
        success: false,
        hasRemoteVelocity: false,
        aheadOfRemote: 0,
        behindRemote: 0,
        remoteVelocityCommit: null,
        localVelocityCommit: '',
        needsPull: false,
        needsPush: false,
        hasRemoteConflicts: false,
        error: `Failed to get remote velocity status (${response.status})`
      };
    }

    return await response.json();
  } catch (error: any) {
    console.error('[Product API] Error getting remote velocity status:', error);
    return {
      success: false,
      hasRemoteVelocity: false,
      aheadOfRemote: 0,
      behindRemote: 0,
      remoteVelocityCommit: null,
      localVelocityCommit: '',
      needsPull: false,
      needsPush: false,
      hasRemoteConflicts: false,
      error: error.message || 'Failed to get remote velocity status'
    };
  }
}

// ============================================================================
// Sync to Main Operations
// ============================================================================

/**
 * Create a velocity pull request (velocity → default branch)
 * 
 * @param repositoryUrl - URL of the repository
 * @param accessToken - Access token for authentication
 * @param title - PR title
 * @param body - Optional PR body/description
 * @param provider - Source control provider ('github' or 'gitlab')
 * @param instanceUrl - Optional GitLab instance URL
 * @param defaultBranch - Target branch name from org settings (defaults to 'main')
 * @returns Result with PR info
 */
export async function createVelocityPR(
  repositoryUrl: string,
  accessToken: string,
  title: string,
  body?: string,
  provider: SourceControlProvider = 'github',
  instanceUrl?: string,
  defaultBranch?: string
): Promise<CreateVelocityPRResult> {
  try {
    const apiEndpoint = provider === 'github' ? 'github' : 'gitlab';
    const apiUrl = `${getProductServerBaseUrl()}/api/${apiEndpoint}/create-velocity-pr`;

    const response = await fetch(apiUrl, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        repositoryUrl,
        accessToken,
        title,
        body,
        instanceUrl,
        defaultBranch
      }),
    });

    if (!response.ok) {
      const errorData = await response.json();
      return {
        success: false,
        error: errorData.error || `Failed to create velocity PR (${response.status})`
      };
    }

    return await response.json();
  } catch (error: any) {
    console.error('[Product API] Error creating velocity PR:', error);
    return {
      success: false,
      error: error.message || 'Failed to create velocity PR'
    };
  }
}

/**
 * Sync velocity branch to the default branch (main/master)
 * 
 * @param repositoryUrl - URL of the repository
 * @param accessToken - Access token for authentication
 * @param provider - Source control provider ('github' or 'gitlab')
 * @param instanceUrl - Optional GitLab instance URL
 * @param organizationId - Organization ID for fetching settings
 * @returns Result with sync action taken
 */
export async function syncVelocityToMain(
  repositoryUrl: string,
  accessToken: string,
  provider: SourceControlProvider = 'github',
  instanceUrl?: string,
  organizationId?: string
): Promise<SyncToMainResult> {
  try {
    const apiEndpoint = provider === 'github' ? 'github' : 'gitlab';
    const apiUrl = `${getProductServerBaseUrl()}/api/${apiEndpoint}/sync-to-main`;

    const response = await fetch(apiUrl, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        repositoryUrl,
        accessToken,
        instanceUrl,
        organizationId
      }),
    });

    if (!response.ok) {
      const errorData = await response.json();
      return {
        success: false,
        action: 'error',
        error: errorData.error || `Failed to sync velocity to main (${response.status})`
      };
    }

    return await response.json();
  } catch (error: any) {
    console.error('[Product API] Error syncing velocity to main:', error);
    return {
      success: false,
      action: 'error',
      error: error.message || 'Failed to sync velocity to main'
    };
  }
}

/**
 * Get analysis for velocity → main sync
 * 
 * @param repositoryUrl - URL of the repository
 * @param provider - Source control provider ('github' or 'gitlab')
 * @returns Sync-to-main analysis and decision
 */
export async function getSyncToMainAnalysis(
  repositoryUrl: string,
  provider: SourceControlProvider = 'github'
): Promise<SyncToMainAnalysisResult> {
  try {
    const apiEndpoint = provider === 'github' ? 'github' : 'gitlab';
    const apiUrl = buildProductApiUrl(`/api/${apiEndpoint}/sync-to-main-analysis`, { repositoryUrl });

    const response = await fetch(apiUrl);

    if (!response.ok) {
      const errorData = await response.json();
      return {
        success: false,
        error: errorData.error || `Failed to get sync-to-main analysis (${response.status})`
      };
    }

    const data = await response.json();
    return {
      success: true,
      analysis: data.analysis,
      decision: data.decision
    };
  } catch (error: any) {
    console.error('[Product API] Error getting sync-to-main analysis:', error);
    return {
      success: false,
      error: error.message || 'Failed to get sync-to-main analysis'
    };
  }
}

// ============================================================================
// Velocity Mode Sync Operations
// ============================================================================

/**
 * Sync velocity branch with remote origin/velocity (velocity mode)
 * 
 * This is the "Google Docs" style sync:
 * 1. Optionally auto-commit uncommitted changes
 * 2. Pull from origin/velocity
 * 3. Push local commits to origin/velocity
 * 
 * @param repositoryUrl - URL of the repository
 * @param accessToken - Access token for authentication
 * @param autoCommit - Whether to auto-commit uncommitted changes before syncing
 * @param provider - Source control provider ('github' or 'gitlab')
 * @param instanceUrl - Optional GitLab instance URL
 * @returns Result with sync details
 */
export async function syncWithRemoteVelocity(
  repositoryUrl: string,
  accessToken: string,
  autoCommit: boolean = true,
  provider: SourceControlProvider = 'github',
  instanceUrl?: string
): Promise<VelocityModeSyncResult> {
  try {
    const apiEndpoint = provider === 'github' ? 'github' : 'gitlab';
    const apiUrl = `${getProductServerBaseUrl()}/api/${apiEndpoint}/velocity-mode-sync`;

    const response = await fetch(apiUrl, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        repositoryUrl,
        accessToken,
        autoCommit,
        instanceUrl
      }),
    });

    if (!response.ok) {
      const errorData = await response.json();
      // Handle conflict response
      if (response.status === 409) {
        return {
          success: false,
          action: 'conflicts',
          message: errorData.message || 'Conflicts detected with remote',
          conflicts: errorData.conflicts,
          error: errorData.error
        };
      }
      return {
        success: false,
        action: 'error',
        error: errorData.error || `Failed to sync with remote velocity (${response.status})`
      };
    }

    return await response.json();
  } catch (error: any) {
    console.error('[Product API] Error syncing with remote velocity:', error);
    return {
      success: false,
      action: 'error',
      error: error.message || 'Failed to sync with remote velocity'
    };
  }
}

// ============================================================================
// Conflict Operations
// ============================================================================

/**
 * Get list of worktrees for a repository
 * 
 * @param repositoryUrl - URL of the repository
 * @param provider - Source control provider ('github' or 'gitlab')
 * @returns List of worktrees
 */
export async function getWorktrees(
  repositoryUrl: string,
  provider: SourceControlProvider = 'github'
): Promise<WorktreesResult> {
  try {
    const apiEndpoint = provider === 'github' ? 'github' : 'gitlab';
    const apiUrl = buildProductApiUrl(`/api/${apiEndpoint}/worktrees`, { repositoryUrl });

    const response = await fetch(apiUrl);

    if (!response.ok) {
      const errorData = await response.json();
      return {
        success: false,
        error: errorData.error || `Failed to get worktrees (${response.status})`
      };
    }

    const data = await response.json();
    return {
      success: true,
      worktrees: data.worktrees || []
    };
  } catch (error: any) {
    console.error('[Product API] Error getting worktrees:', error);
    return {
      success: false,
      error: error.message || 'Failed to get worktrees'
    };
  }
}

/**
 * Result type for delete worktree operation
 */
export interface DeleteWorktreeResult {
  success: boolean;
  message?: string;
  worktreePath?: string;
  error?: string;
}

/**
 * Delete a worktree from a repository
 * 
 * @param repositoryUrl - URL of the repository
 * @param worktreePath - Path to the worktree to delete
 * @param force - Force deletion even if worktree has uncommitted changes
 * @param provider - Source control provider ('github' or 'gitlab')
 * @returns Result of the delete operation
 */
export async function deleteWorktree(
  repositoryUrl: string,
  worktreePath: string,
  force: boolean = false,
  provider: SourceControlProvider = 'github'
): Promise<DeleteWorktreeResult> {
  try {
    const apiEndpoint = provider === 'github' ? 'github' : 'gitlab';
    const apiUrl = `${getProductServerBaseUrl()}/api/${apiEndpoint}/worktrees`;

    const response = await fetch(apiUrl, {
      method: 'DELETE',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        repositoryUrl,
        worktreePath,
        force
      }),
    });

    if (!response.ok) {
      const errorData = await response.json();
      return {
        success: false,
        error: errorData.message || errorData.error || `Failed to delete worktree (${response.status})`
      };
    }

    return await response.json();
  } catch (error: any) {
    console.error('[Product API] Error deleting worktree:', error);
    return {
      success: false,
      error: error.message || 'Failed to delete worktree'
    };
  }
}

/**
 * Check for conflicts between velocity and main branches
 * 
 * @param repositoryUrl - URL of the repository
 * @param provider - Source control provider ('github' or 'gitlab')
 * @returns Conflicts result
 */
export async function checkConflicts(
  repositoryUrl: string,
  provider: SourceControlProvider = 'github'
): Promise<ConflictsResult> {
  try {
    const apiEndpoint = provider === 'github' ? 'github' : 'gitlab';
    const apiUrl = buildProductApiUrl(`/api/${apiEndpoint}/conflicts`, { repositoryUrl });

    const response = await fetch(apiUrl);

    if (!response.ok) {
      const errorData = await response.json();
      return {
        success: false,
        hasConflicts: false,
        error: errorData.error || `Failed to check conflicts (${response.status})`
      };
    }

    return await response.json();
  } catch (error: any) {
    console.error('[Product API] Error checking conflicts:', error);
    return {
      success: false,
      hasConflicts: false,
      error: error.message || 'Failed to check conflicts'
    };
  }
}

/**
 * Batch resolve conflicts using AI
 * 
 * @param conflicts - Array of conflicts to resolve
 * @param autoApplyThreshold - Confidence threshold for auto-applying (default 0.8)
 * @param provider - Source control provider ('github' or 'gitlab')
 * @returns Batch resolution result
 */
export async function batchResolveConflicts(
  conflicts: Array<{
    filePath: string;
    entityId: string;
    entityType: 'issue' | 'user-story' | 'epic';
    documentType: 'issues' | 'user-stories';
    baseVersion: string;
    localVersion: string;
    remoteVersion: string;
  }>,
  autoApplyThreshold: number = 0.8,
  provider: SourceControlProvider = 'github'
): Promise<BatchResolveResult> {
  try {
    const apiEndpoint = provider === 'github' ? 'github' : 'gitlab';
    const apiUrl = `${getProductServerBaseUrl()}/api/${apiEndpoint}/resolve-all`;

    const response = await fetch(apiUrl, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        conflicts,
        autoApplyThreshold
      }),
    });

    if (!response.ok) {
      const errorData = await response.json();
      return {
        success: false,
        resolved: [],
        failed: conflicts.map(c => ({ entityId: c.entityId, error: errorData.error || 'Resolution failed' })),
        summary: {
          total: conflicts.length,
          resolved: 0,
          autoApplied: 0,
          needsReview: 0,
          failed: conflicts.length
        }
      };
    }

    return await response.json();
  } catch (error: any) {
    console.error('[Product API] Error batch resolving conflicts:', error);
    return {
      success: false,
      resolved: [],
      failed: conflicts.map(c => ({ entityId: c.entityId, error: error.message || 'Resolution failed' })),
      summary: {
        total: conflicts.length,
        resolved: 0,
        autoApplied: 0,
        needsReview: 0,
        failed: conflicts.length
      }
    };
  }
}

/**
 * Resolve a single conflict using AI
 * 
 * @param conflict - Conflict to resolve
 * @param repositoryUrl - URL of the repository
 * @param provider - Source control provider ('github' or 'gitlab')
 * @returns AI resolution result with suggested resolution
 */
export async function resolveConflict(
  conflict: {
    filePath: string;
    entityId: string;
    entityType: 'issue' | 'user-story' | 'epic';
    documentType: 'issues' | 'user-stories';
    baseVersion: string;
    localVersion: string;
    remoteVersion: string;
  },
  repositoryUrl: string,
  provider: SourceControlProvider = 'github'
): Promise<AIResolutionResult> {
  try {
    const apiEndpoint = provider === 'github' ? 'github' : 'gitlab';
    const apiUrl = `${getProductServerBaseUrl()}/api/${apiEndpoint}/resolve-conflict`;

    const response = await fetch(apiUrl, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        repositoryUrl,
        ...conflict
      }),
    });

    if (!response.ok) {
      const errorData = await response.json();
      return {
        success: false,
        confidence: 0,
        reasoning: '',
        changes: [],
        error: errorData.error || errorData.message || `Failed to resolve conflict (${response.status})`
      };
    }

    return await response.json();
  } catch (error: any) {
    console.error('[Product API] Error resolving conflict:', error);
    return {
      success: false,
      confidence: 0,
      reasoning: '',
      changes: [],
      error: error.message || 'Failed to resolve conflict'
    };
  }
}

/**
 * Apply conflict resolutions to repository files
 * 
 * @param repositoryUrl - URL of the repository
 * @param resolutions - Array of resolutions to apply
 * @param autoCommit - Whether to automatically commit the changes
 * @param commitMessage - Custom commit message
 * @param provider - Source control provider ('github' or 'gitlab')
 * @returns Result with files modified and any errors
 */
export async function applyResolutions(
  repositoryUrl: string,
  resolutions: Array<{
    filePath: string;
    entityId: string;
    entityType: 'issue' | 'user-story' | 'epic';
    documentType: 'issues' | 'user-stories';
    resolvedContent: string;
    method: 'local' | 'remote' | 'ai' | 'manual';
  }>,
  autoCommit: boolean = true,
  commitMessage?: string,
  provider: SourceControlProvider = 'github'
): Promise<ApplyResolutionsResult> {
  try {
    const apiEndpoint = provider === 'github' ? 'github' : 'gitlab';
    const apiUrl = `${getProductServerBaseUrl()}/api/${apiEndpoint}/apply-resolutions`;

    const response = await fetch(apiUrl, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        repositoryUrl,
        resolutions,
        autoCommit,
        commitMessage: commitMessage || `Resolve ${resolutions.length} merge conflict(s)`
      }),
    });

    if (!response.ok) {
      const errorData = await response.json();
      return {
        success: false,
        filesModified: [],
        filesStaged: [],
        errors: [{ filePath: '', entityId: '', error: errorData.error || errorData.message || 'Failed to apply resolutions' }]
      };
    }

    return await response.json();
  } catch (error: any) {
    console.error('[Product API] Error applying resolutions:', error);
    return {
      success: false,
      filesModified: [],
      filesStaged: [],
      errors: [{ filePath: '', entityId: '', error: error.message || 'Failed to apply resolutions' }]
    };
  }
}

// ============================================================================
// Repository Reset Operations
// ============================================================================

/**
 * Check for local changes on the velocity branch before resetting
 * 
 * This helps warn users about uncommitted changes, commits ahead of main,
 * and unpushed commits that would be lost during a reset.
 * 
 * @param repositoryUrl - URL of the repository
 * @param provider - Source control provider ('github' or 'gitlab')
 * @returns Status of local changes
 */
export async function checkLocalChanges(
  repositoryUrl: string,
  provider: SourceControlProvider = 'github'
): Promise<CheckLocalChangesResult> {
  try {
    const apiEndpoint = provider === 'github' ? 'github' : 'gitlab';
    const apiUrl = buildProductApiUrl(`/api/${apiEndpoint}/check-local-changes`, { repositoryUrl });

    const response = await fetch(apiUrl);

    if (!response.ok) {
      const errorData = await response.json();
      return {
        success: false,
        error: errorData.error || `Failed to check local changes (${response.status})`
      };
    }

    return await response.json();
  } catch (error: any) {
    console.error('[Product API] Error checking local changes:', error);
    return {
      success: false,
      error: error.message || 'Failed to check local changes'
    };
  }
}

/**
 * Reset the local repository by wiping it and re-cloning from remote
 * 
 * This is useful when:
 * - The local repo was manually deleted from ~/.velocity/repos
 * - The local repo has become corrupted
 * - The user wants to start fresh with the remote state
 * 
 * @param repositoryUrl - URL of the repository
 * @param accessToken - Optional access token for private repos
 * @param provider - Source control provider ('github' or 'gitlab')
 * @param instanceUrl - Optional GitLab instance URL
 * @param force - Force reset even if there are local changes
 * @returns Result of the reset operation
 */
export async function resetLocalRepository(
  repositoryUrl: string,
  accessToken?: string | null,
  provider: SourceControlProvider = 'github',
  instanceUrl?: string,
  force: boolean = false
): Promise<ResetRepoResult> {
  try {
    const apiEndpoint = provider === 'github' ? 'github' : 'gitlab';
    const apiUrl = `${getProductServerBaseUrl()}/api/${apiEndpoint}/reset-repo`;

    const response = await fetch(apiUrl, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        repositoryUrl,
        accessToken: accessToken || null,
        instanceUrl,
        force
      }),
    });

    const data = await response.json();

    if (response.status === 409) {
      // Local changes detected and force was not set
      return {
        success: false,
        action: 'skipped',
        message: data.message || 'Local changes detected. Confirm to reset anyway.',
        lostChanges: data.lostChanges,
        error: data.error
      };
    }

    if (!response.ok) {
      return {
        success: false,
        action: 'error',
        error: data.error || `Failed to reset repository (${response.status})`
      };
    }

    return data;
  } catch (error: any) {
    console.error('[Product API] Error resetting repository:', error);
    return {
      success: false,
      action: 'error',
      error: error.message || 'Failed to reset repository'
    };
  }
}
